<template>
  <div>
    <v-list three-line>
      <template v-for="comment of sortedThread">
        <v-list-item :key="comment.id">
          <v-list-item-icon>
            <v-icon color="primary"> mdi-comment-outline </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-subtitle
              class="thread-box rounded-lg rounded-b-0 pt-4 px-4 ma-0"
              :class="{
                'font-weight-bold': comment.creator.email === user.email,
              }"
            >
              <span v-if="comment.creator.email === user.email">
                {{ $t("common.label.me") }}
                (<span
                  v-if="comment.creator.displayedName"
                  :title="comment.creator.email"
                >
                  {{ comment.creator.displayedName }}
                </span>
                <span v-else>{{ comment.creator.email }}</span
                >)
              </span>
              <span
                v-else-if="comment.creator.displayedName"
                :title="comment.creator.email"
              >
                {{ comment.creator.displayedName }}
              </span>
              <span v-else>
                {{ comment.creator.email }}
              </span>
            </v-list-item-subtitle>
            <v-list-item-title
              class="thread-box rounded-lg rounded-t-0 py-4 px-4"
            >
              <template
                v-if="
                  commentToEdit !== null &&
                  commentToEdit.id === comment.id &&
                  comment.creator.email === user.email
                "
              >
                <v-textarea
                  rows="3"
                  v-model="commentToEdit.content"
                ></v-textarea>
              </template>
              <template v-else>
                <p class="text-content">{{ comment.content }}</p>
              </template>
            </v-list-item-title>
            <v-list-item-subtitle>
              <template
                v-if="
                  commentToEdit !== null &&
                  commentToEdit.id === comment.id &&
                  (comment.creator.email === user.email ||
                    user.hasRight(ACLRight.COMMENT_UPDATE_VISIBILITY))
                "
              >
                <CommentVisibilitySelector
                  v-if="canUpdateVisibility"
                  v-model="commentToEdit.visibility"
                ></CommentVisibilitySelector>
                <v-btn text small @click="cancelEdit">
                  {{ $t("common.action.cancel") }}
                </v-btn>
                <v-btn small color="accent" @click="saveCommentToEdit">
                  <v-icon>mdi-check</v-icon>
                  {{ $t("common.action.save") }}
                </v-btn>
              </template>
              <template v-else>
                <div
                  class="comment-meta d-flex align-center text-caption accent--text"
                >
                  <CommentVisibilityIcon
                    v-if="canUpdateVisibility"
                    :visibility="comment.visibility"
                    show-label
                    class="mr-3"
                  ></CommentVisibilityIcon>
                  <span class="text-no-wrap ml-auto">
                    {{ comment.createdAt.toLocaleDateString($i18n.locale) }}
                    {{ comment.createdAt.toLocaleTimeString() }}
                  </span>
                </div>
                <div class="d-flex align-center mt-2">
                  <v-btn
                    outlined
                    small
                    class="btn-outline-accent mr-2"
                    @click="editComment(comment)"
                    v-if="
                      user.hasRight(ACLRight.COMMENT_UPDATE_VISIBILITY) ||
                      user.email === comment.creator.email
                    "
                  >
                    {{ $t("common.action.edit") }}
                  </v-btn>
                  <v-btn
                    outlined
                    small
                    class="btn-outline-accent mr-2"
                    @click="emitReply(comment.id, comment.visibility)"
                  >
                    {{ $t("comment.action.reply") }}
                  </v-btn>
                  <v-btn
                    outlined
                    small
                    color="error"
                    class="btn-outline-error ml-auto"
                    @click="openDeleteDialog(comment)"
                    v-if="
                      user.hasRight(ACLRight.COMMENT_DELETE) ||
                      user.email === comment.creator.email
                    "
                  >
                    <v-icon small class="mr-1">mdi-delete-outline</v-icon>
                    {{ $t("common.action.delete") }}
                  </v-btn>
                </div>
              </template>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>
    <v-dialog v-model="confirmDeleteDialog" persistent width="auto">
      <ConfirmationPrompt
        :title="$t('common.prompt.confirmDelete')"
        :confirmLabel="$t('common.action.delete')"
        confirmColor="error"
        @cancel="closeDeleteDialog"
        @confirm="confirmDelete"
      ></ConfirmationPrompt>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { ACLRight } from "@/api/acl-right.enum";
import { CommentOutDTO } from "@/api/models/comment/comment.out.dto";
import { UserWithRightOutDTO } from "@/api/models/user/user-with-right.out";
import { StoreGetter } from "@/store/storeGetter.decorator";
import { CommentVisibility } from "@/api/models/comment/type/comment-visibility.enum";
import Vue from "vue";
import Component from "vue-class-component";
import { Emit, Prop } from "vue-property-decorator";
import ConfirmationPrompt from "../ConfirmationPrompt.vue";
import CommentVisibilityIcon from "./CommentVisibilityIcon.vue";
import CommentVisibilitySelector from "./CommentVisibilitySelector.vue";

@Component({
  components: {
    CommentVisibilityIcon,
    ConfirmationPrompt,
    CommentVisibilitySelector,
  },
})
export default class CommentThread extends Vue {
  @StoreGetter("user/user") user!: UserWithRightOutDTO;

  @Prop({ type: Array, default: () => [] })
  public readonly thread!: CommentOutDTO[];

  @Prop({ type: Boolean, default: true })
  public readonly canUpdateVisibility!: boolean;

  @Emit("reply")
  emitReply(commentId: string, commentVisibility: CommentVisibility) {
    return { commentId, commentVisibility };
  }

  @Emit("delete")
  emitDelete() {
    return this.commentToDelete?.id;
  }

  @Emit("deleteMine")
  emitDeleteMine() {
    return this.commentToDelete?.id;
  }

  @Emit("update")
  emitUpdate(comment: CommentOutDTO) {
    return comment;
  }

  @Emit("updateMine")
  emitUpdateMine(comment: CommentOutDTO) {
    return comment;
  }

  public ACLRight = ACLRight;
  public confirmDeleteDialog = false;
  public commentToDelete: CommentOutDTO | null = null;
  public commentToEdit: CommentOutDTO | null = null;

  public get sortedThread() {
    return this.thread.sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
  }

  public openDeleteDialog(comment: CommentOutDTO) {
    this.commentToDelete = comment;
    this.confirmDeleteDialog = true;
  }

  public closeDeleteDialog() {
    this.commentToDelete = null;
    this.confirmDeleteDialog = false;
  }

  public confirmDelete() {
    if (this.user.hasRight(ACLRight.COMMENT_DELETE)) {
      this.emitDelete();
    } else if (this.commentToDelete?.creator.email === this.user.email) {
      this.emitDeleteMine();
    }
    this.closeDeleteDialog();
  }

  public editComment(comment: CommentOutDTO) {
    this.commentToEdit = { ...comment };
  }

  public saveCommentToEdit() {
    if (this.commentToEdit) {
      if (this.commentToEdit.creator.email === this.user.email) {
        this.emitUpdateMine(this.commentToEdit);
      } else if (this.user.hasRight(ACLRight.COMMENT_UPDATE_VISIBILITY)) {
        this.emitUpdate(this.commentToEdit);
      }
      this.cancelEdit();
    }
  }

  public cancelEdit() {
    this.commentToEdit = null;
  }
}
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_colors";

.thread-box {
  background-color: map-get($grey, "lighten-4");
}

.theme--dark {
  .thread-box {
    background-color: map-get($grey, "darken-4");
  }
}

// Le pied de page (accent--text) est orange ; on force l'icône de visibilité à
// hériter cette couleur (sinon le v-icon garde sa couleur de thème grise).
.comment-meta ::v-deep .v-icon {
  color: inherit !important;
}
</style>
